import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../../api/axios';
import { useLoading } from '../../../../components/LoadingProvider';
import useAuth from '../../../../hooks/useAuth';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates } from '../../../../utils/common';

export default function DoctorAppointmentPhysiotherapyPrescriptionPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    document.title = `Varosa - Doctor Appointment Physiotherapy Prescription No ${id}`;
    const { setIsLoading } = useLoading();
    const { userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const [ appointmentPrescriptionData, setAppointmentPrescriptionData ] = useState(null);
    const [ oldAppointments, setOldAppointments ] = useState([]);
    const prefixOfName = [ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ];
    const [ accordionStatus, setAccordionStatus ] = useState({
        one: true,
    });
    const formPatientRef = useRef(null);
    const formRef = useRef(null);
    const indStates = getIndStates();
    const [ prescriptionFormData, setPrescriptionFormData ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_line2: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        chief_complaints: '',
        hopi: '',
        examination: '',
        management: '',
        diagnosis: '',
        remarks: '',
        is_completed: 'Yes',
        prescription_type: 'physiotherapy',
    });
    const [ prescriptionFormError, setPrescriptionFormError ] = useState({
        patient_name: '',
        patient_name_prefix: '',
        patient_dob: '',
        patient_gender: '',
        patient_address_line1: '',
        patient_address_district: '',
        patient_address_state: '',
        patient_address_pincode: '',
        chief_complaints: '',
        hopi: '',
        examination: '',
        management: '',
        diagnosis: '',
        remarks: '',
        is_completed: '',
    });
    useEffect(() => {
        fetchAppointmentPrescription();
    }, []);
    const fetchAppointmentPrescription = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/physiotherapy-prescription`);
            if (response?.data?.result === 'success') {
                setAppointmentPrescriptionData(response?.data?.data);
                setOldAppointments(response?.data?.data.old_appointments);
                let found_state = response?.data?.data?.patient?.state || '';
                if(found_state.length > 2){
                    let ind_state = indStates.find(s => {
                        return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                    });
                    if(ind_state){
                        found_state = ind_state.key;
                    }
                }
                setPrescriptionFormData({
                    patient_name: response?.data?.data?.patient?.name || '',
                    patient_name_prefix: response?.data?.data?.patient?.name_prefix || '',
                    patient_dob: response?.data?.data?.patient?.dob || '',
                    patient_gender: response?.data?.data?.patient?.gender || '',
                    patient_address_line1: response?.data?.data?.patient?.address_line1 || '',
                    patient_address_line2: response?.data?.data?.patient?.address_line2 || '',
                    patient_address_district: response?.data?.data?.patient?.district || '',
                    patient_address_state: found_state,
                    patient_address_pincode: response?.data?.data?.patient?.pincode || '',
                    patient_address_country: response?.data?.data?.patient?.country || '',
                    chief_complaints: response?.data?.data?.appointment_prescription?.chief_complaints || '',
                    hopi: response?.data?.data?.appointment_prescription?.hopi || '',
                    examination: response?.data?.data?.appointment_prescription?.examination || '',
                    management: response?.data?.data?.appointment_prescription?.management || '',
                    diagnosis: response?.data?.data?.appointment_prescription?.diagnosis || '',
                    med_rx: response?.data?.data?.appointment_prescription?.med_rx || '',
                    med_rx_arr: response?.data?.data?.appointment_prescription?.med_rx_products || [],
                    remarks: response?.data?.data?.appointment_prescription?.remarks || '',
                    is_completed: 'Yes',
                    prescription_type: 'physiotherapy',
                });
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const handleAppointmentDateChange = async (e) => {
        const { name, value } = e.target;
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}/old-prescription?type=physiotherapy&search_date=`+value);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setOldAppointments(response?.data?.data.old_appointments);
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const handlePrescription = async () => {
        try {
            const resetPrescriptionFormError = {
                patient_name: '',
                patient_name_prefix: '',
                patient_dob: '',
                patient_gender: '',
                patient_address_line1: '',
                patient_address_line2: '',
                patient_address_district: '',
                patient_address_state: '',
                patient_address_pincode: '',
                chief_complaints: '',
                hopi: '',
                examination: '',
                management: '',
                diagnosis: '',
                med_rx: ''
            };
            setPrescriptionFormError(resetPrescriptionFormError);
            if (!prescriptionFormData?.patient_name) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name: 'Patient Name is required' });
                return;
            }
            if (!prescriptionFormData?.patient_name_prefix) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_name_prefix: 'Patient Name Prefix is required' });
                return;
            }
            if (!prescriptionFormData?.patient_dob) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_dob: 'Patient DOB is required' });
                return;
            }
            if (!prescriptionFormData?.patient_gender) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_gender: 'Patient Gender is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_line1) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_line1: 'Patient Address line 1 is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_district) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_district: 'Patient District is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_state) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_state: 'Patient State is required' });
                return;
            }
            if (!prescriptionFormData?.patient_address_pincode) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, patient_address_pincode: 'Patient Pincode is required' });
                return;
            }
            if (!prescriptionFormData?.chief_complaints) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, chief_complaints: 'Chief complaints is required' });
                return;
            }
            if (!prescriptionFormData?.hopi) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, hopi: 'HOPI (History of present illness) is required' });
                return;
            }
            if (!prescriptionFormData?.examination) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, examination: 'Examination is required' });
                return;
            }
            if (!prescriptionFormData?.management) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, management: 'Management is required' });
                return;
            }
            if (!prescriptionFormData?.diagnosis) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, diagnosis: 'Diagnosis is required' });
                return;
            }
            if (!prescriptionFormData?.is_completed) {
                setPrescriptionFormError({ ...resetPrescriptionFormError, is_completed: 'Appointment Completed is required' });
                return;
            }
            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/physiotherapy-prescription`, prescriptionFormData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchAppointmentPrescription();  
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors?.redirect){
                    navigate(`/dashboard/${userRole}/appointment/${id}`);
                }else if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <>
            <div className="container py-5 login-container appointment-detail">
                <div className="row g-3">
                    <div className="col-8">
                        <h2 className="mb-4">Appointment Physiotherapy Prescription No {id}</h2>
                    </div>
                    <div className="col-4 text-end">
                        <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                    </div>
                    <hr />
                </div>
                <div className="row g-3">
                    <div className="col-md-12">
                        <div className="accordion">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingOne" 
                                    onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne" aria-expanded="true" aria-controls="panelsStayOpen-collapseOne">
                                        Previous Appointments
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingOne">
                                    <div className="accordion-body">
                                        <div className="row">
                                            <div className="col-12">
                                                <label className="form-label">Select Appointment Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control py-3 border-primary"
                                                    name="appointment_date"
                                                    onChange={handleAppointmentDateChange}
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                            <hr className="mt-4 mb-3" />
                                            <div className="col-12">
                                                {oldAppointments.length > 0 ?
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col" style={{ width: '10%' }}>IDs</th>
                                                            <th scope="col" style={{ width: '30%' }}>Appointment Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Patient Details</th>
                                                            <th scope="col" style={{ width: '30%' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {oldAppointments?.map((item, index) => (<tr key={index}>
                                                            <th scope="row" style={{ width: '10%' }}>{item.appointment.id}</th>
                                                            <td style={{ width: '30%' }}>
                                                                Date & Time: <b>{item.appointment.appointment_date_time}</b><br />
                                                                Appointment Completed: <b>{item.appointment.is_completed}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                Name: <b>{item.appointment.patient_name}</b><br />
                                                                Age: <b>{item.appointment.patient_age} Yrs</b><br />
                                                                Gender: <b>{item.appointment.patient_gender.toUpperCase()}</b><br />
                                                                Address: <b>{item.appointment.patient_address}</b>
                                                            </td>
                                                            <td style={{ width: '30%' }}>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-primary btn-sm mt-1 mb-1 me-1"
                                                                    onClick={(e) => {
                                                                        let found_state = item.patient.state || '';
                                                                        if(found_state.length > 2){
                                                                            let ind_state = indStates.find(s => {
                                                                                return found_state.replaceAll(' ', '').toLocaleLowerCase() === s.name.replaceAll(' ', '').toLocaleLowerCase();
                                                                            });
                                                                            if(ind_state){
                                                                                found_state = ind_state.key;
                                                                            }
                                                                        }
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            patient_name_prefix: item.patient.name_prefix,
                                                                            patient_name: item.patient.name,
                                                                            patient_dob: item.patient.dob,
                                                                            patient_gender: item.patient.gender,
                                                                            patient_address_line1: item.patient.address_line1,
                                                                            patient_address_line2: item.patient.address_line2,
                                                                            patient_address_district: item.patient.district,
                                                                            patient_address_state: found_state,
                                                                            patient_address_pincode: item.patient.pincode,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            patient_name_prefix: '',
                                                                            patient_name: '',
                                                                            patient_dob: '',
                                                                            patient_gender: '',
                                                                            patient_address_line1: '',
                                                                            patient_address_district: '',
                                                                            patient_address_state: '',
                                                                            patient_address_pincode: '',
                                                                        });
                                                                        formPatientRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Patient Data</button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-secondary btn-sm mt-1 mb-1 me-1" 
                                                                    onClick={(e) => {
                                                                        setPrescriptionFormData({
                                                                            ...prescriptionFormData,
                                                                            chief_complaints: item.appointment_prescription.chief_complaints,
                                                                            hopi: item.appointment_prescription.hopi,
                                                                            examination: item.appointment_prescription.examination,
                                                                            diagnosis: item.appointment_prescription.diagnosis,
                                                                            management: item.appointment_prescription.management,
                                                                        });
                                                                        setPrescriptionFormError({
                                                                            ...prescriptionFormError,
                                                                            chief_complaints: '',
                                                                            hopi: '',
                                                                            examination: '',
                                                                            diagnosis: '',
                                                                            management: '',
                                                                        });
                                                                        formRef.current?.scrollIntoView({ behavior: 'smooth' });
                                                                    }}
                                                                >Copy Prescription Form Data</button>
                                                            </td>
                                                        </tr>))}
                                                    </tbody>
                                                </table> : <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                                    Previous Appointments not found!
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formPatientRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Patient ID:</strong> {appointmentPrescriptionData?.appointment?.patient_id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation ID:</strong> {appointmentPrescriptionData?.appointment?.id}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Consultation Date:</strong> {appointmentPrescriptionData?.appointment?.appointment_date_time}
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <strong>Doctor Name:</strong> {appointmentPrescriptionData?.appointment?.doctor_name}
                                        </p>
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <label className="form-label">Name</label>
                                        <div className="input-group mb-3">
                                            <select 
                                                className="form-select py-3 border-primary"
                                                name="namePrefix"
                                                value={prescriptionFormData?.patient_name_prefix}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name_prefix: e.target.value })}
                                                style={{ maxWidth: '120px' }}
                                            >
                                                <option value="">Choose...</option>
                                                {prefixOfName.map((prefix) => (
                                                    <option key={prefix} value={prefix}>{prefix}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="text"
                                                className="form-control py-3 border-primary"
                                                name="name"
                                                value={prescriptionFormData?.patient_name}
                                                onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        {prescriptionFormError.patient_name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                                        {prescriptionFormError.patient_name_prefix && <div className="invalid-feedback d-block">{prescriptionFormError.patient_name_prefix}</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">DOB</label>
                                        <input
                                            type="date"
                                            className="form-control py-3 border-primary"
                                            name="dob"
                                            value={prescriptionFormData?.patient_dob}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_dob: e.target.value })}
                                            required
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        {prescriptionFormError.patient_dob && <div className="invalid-feedback d-block">Please enter a valid DOB.</div>}
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Gender</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="gender"
                                            value={prescriptionFormData?.patient_gender}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_gender: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                        {prescriptionFormError.patient_gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                                    </div>

                                    {/* Address Fields */}
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 1</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line1"
                                            value={prescriptionFormData?.patient_address_line1}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line1: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 2 (optional)</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_line2"
                                            value={prescriptionFormData?.patient_address_line2}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_line2: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">District</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_district"
                                            value={prescriptionFormData?.patient_address_district}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_district: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">State</label>
                                        <select
                                            className="form-select py-3 border-primary"
                                            name="address.state"
                                            value={prescriptionFormData?.patient_address_state}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_state: e.target.value })}
                                            required
                                        >
                                            <option value="">Choose...</option>
                                            {indStates.map((val) => (<option key={val.key} value={val.key}>{val.name}</option>))}
                                        </select>
                                        {prescriptionFormError.patient_address_state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Pincode</label>
                                        <input
                                            type="text"
                                            className="form-control py-3 border-primary"
                                            name="patient_address_pincode"
                                            value={prescriptionFormData?.patient_address_pincode}
                                            onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, patient_address_pincode: e.target.value })}
                                            required
                                        />
                                        {prescriptionFormError.patient_address_pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                                    </div>
                                    <hr />
                                    <div className="col-md-6">
                                        <p className="card-text">
                                            <b>Support & Helpline (10am-6pm):</b> +91 8240112175 <br /><b>Office Number:</b> +91 3482329183 
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12" ref={formRef}>
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title">Prescription Form</h5>
                            </div>
                            <div className="card-body">
                                <form>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="chief_complaints" className="form-label">Chief compliants</label>
                                                <textarea className="form-control" id="chief_complaints" rows="3" value={prescriptionFormData?.chief_complaints} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, chief_complaints: e.target.value })}></textarea>
                                                {prescriptionFormError?.chief_complaints && <div className="text-danger">{prescriptionFormError?.chief_complaints}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="hopi" className="form-label">HOPI (History of present illness)</label>
                                                <textarea className="form-control" id="hopi" rows="3" value={prescriptionFormData?.hopi} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, hopi: e.target.value })}></textarea>
                                                {prescriptionFormError?.hopi && <div className="text-danger">{prescriptionFormError?.hopi}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="examination" className="form-label">Examination</label>
                                                <textarea className="form-control" id="examination" rows="3" value={prescriptionFormData?.examination} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, examination: e.target.value })}></textarea>
                                                {prescriptionFormError?.examination && <div className="text-danger">{prescriptionFormError?.examination}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="diagnosis" className="form-label">Diagnosis</label>
                                                <textarea className="form-control" id="diagnosis" rows="3" value={prescriptionFormData?.diagnosis} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, diagnosis: e.target.value })}></textarea>
                                                {prescriptionFormError?.diagnosis && <div className="text-danger">{prescriptionFormError?.diagnosis}</div>}
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="management" className="form-label">Management</label>
                                                <textarea className="form-control" id="management" rows="3" value={prescriptionFormData?.management} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, management: e.target.value })}></textarea>
                                                {prescriptionFormError?.management && <div className="text-danger">{prescriptionFormError?.management}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">REMARKS</label>
                                                <textarea className="form-control" id="remarks" rows="3" value={prescriptionFormData?.remarks} onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, remarks: e.target.value })}></textarea>
                                                {prescriptionFormError?.remarks && <div className="text-danger">{prescriptionFormError?.remarks}</div>}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="remarks" className="form-label">Appointment Completed</label>
                                                <div className="input-group mb-3">
                                                    <select 
                                                        className="form-select border-primary" 
                                                        name="isCompleted" 
                                                        value={prescriptionFormData?.is_completed}
                                                        onChange={(e) => setPrescriptionFormData({ ...prescriptionFormData, is_completed: e.target.value })}
                                                    >
                                                        {['Yes','No'].map((item) => (
                                                            <option key={item} value={item}>{item}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {prescriptionFormError?.is_completed && <div className="text-danger">{prescriptionFormError?.is_completed}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <button type="button" className="btn btn-primary float-end mt-5" onClick={handlePrescription}>Submit</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}